<template>
    <div :id="`article-${postData?.id}`" class="elle-news-single elle-single">
        <section class="elle-news-single-wrapper padd-v">
            <div class="container">
                <div class="grid12">
                    <div class="l-col">
                        <div class="page-media">
                            <UiImage :src="heroImage" />
                        </div>
                    </div>
                    <div class="m-col">
                        <UiHeadline
                            :settings="{
                                label: postData?.label,
                                headline: postData?.headline
                            }"
                        >
                            <template #additional>
                                <div v-if="postTags.length" class="categories">
                                    <div v-for="(tag, index) in postTags" :key="tag.id ?? tag.slug ?? `tag-${index}`" class="category">
                                        {{ tag.title }}
                                    </div>
                                </div>
                            </template>
                        </UiHeadline>
                    </div>
                    <div v-if="postContent.length" class="r-col">
                        <CommonContent :data="postContent" />
                    </div>
                </div>
            </div>
        </section>

        <ElementsRelatedPosts v-if="relatedPosts.length" :related-posts="relatedPosts" />
    </div>
</template>

<script setup lang="ts">
import type { PostQuery } from '#gql';
import type { CmsItem } from '~/types/cms';

definePageMeta({
    pageTransition: usePageTransition()
});

type NewsPostEntry = Extract<NonNullable<PostQuery['post']>, { relatedPosts?: unknown }>;
type NewsTag = NonNullable<NonNullable<NewsPostEntry['newsGroup']>[number]>;
type NewsMedia = NonNullable<NonNullable<NewsPostEntry['media']>[number]>;
type RelatedPost = NonNullable<NonNullable<NewsPostEntry['relatedPosts']>[number]>;

function isDefined<T>(value: T | null | undefined): value is T {
    return value != null;
}

const route = useRoute();
const setI18nParams = useSetI18nParams();

const { data: post } = await useAsyncGql({
    operation: 'post',
    variables: {
        siteId: useSiteId(),
        slug: route.params.slug
    }
});

const postData = computed<NewsPostEntry | null>(() => {
    const entry = post.value?.post;

    return entry && 'relatedPosts' in entry ? entry : null;
});

const postMedia = computed<NewsMedia[]>(() => postData.value?.media?.filter(isDefined) ?? []);
const postTags = computed<NewsTag[]>(() => postData.value?.newsGroup?.filter(isDefined) ?? []);
const postContent = computed<CmsItem[]>(() => ((postData.value?.content as (CmsItem | null)[] | null) ?? []).filter(isDefined));
const relatedPosts = computed<RelatedPost[]>(() => postData.value?.relatedPosts?.filter(isDefined) ?? []);
const heroImage = computed(() => postMedia.value[0]?.webp ?? undefined);

if (postData.value) {
    route.meta.nuxtI18n = useLocalizedSlug(postData.value);
    setI18nParams(useLocalizedSlug(postData.value));
    usePageSettings(postData.value.typeHandle, postData.value.sectionHandle);
    useSeoData(postData.value.seo);
} else {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found' });
}
</script>
